import _ from 'lodash';
import { OrgCredential } from '../interfaces/organization';
import { getTimestamp } from './timeUtils';

/**
 * Filters out invalid credentials, keeping only those that are active and have required fields
 * @param credentials - Array of credentials to filter
 * @returns Array of valid credentials
 */
export const filterValidCredentials = (
  credentials: OrgCredential[]
): OrgCredential[] => {
  return credentials.filter((cred) => {
    return (
      (cred.active === true || cred.active === false) &&
      cred.status &&
      cred.user_id &&
      cred.organization_id &&
      cred.environment_type &&
      cred.hostname
    );
  });
};

const _getCreditialPriority = (cred: OrgCredential): number => {
  if (cred.source_id) {
    return 2;
  }
  if (cred.is_shared) {
    return 1;
  }
  return 0;
};

export const filterOutDuplicateCredentials = (
  credentials: OrgCredential[],
  currentUserId?: string | null
): OrgCredential[] => {
  const map = new Map();
  credentials.forEach((cred) => {
    const credIdentity = `${cred.organization_id}-${cred.user_id}`;
    const existingCred = map.get(credIdentity);
    if (
      !existingCred ||
      _getCreditialPriority(existingCred) < _getCreditialPriority(cred)
    ) {
      map.set(credIdentity, cred);
    }
  });
  let result = Array.from(map.values()) as OrgCredential[];

  // If currentUserId is provided, filter out shared orgs (not owned by user)
  // when the user already has any connection to the same SF org
  if (currentUserId) {
    // Build a set of organization_ids where the current user has a connection
    const userOrgIds = new Set(
      result
        .filter((c) => c.cirra_user_id === currentUserId)
        .map((c) => c.organization_id)
    );

    // Filter out shared orgs (not owned by current user) if user already has
    // a connection to the same organization_id
    result = result.filter((cred) => {
      const isSharedOrgNotOwned =
        cred.is_shared && cred.cirra_user_id !== currentUserId;
      if (isSharedOrgNotOwned && userOrgIds.has(cred.organization_id)) {
        return false; // Filter out - user already has a connection to this org
      }
      return true;
    });
  }

  return result;
};

/**
 * Filters out orphan clones whose source_id points to a credential not in the provided lookup list.
 * This handles the case where a shared connection was removed but clones still reference it.
 * @param credentials - The credentials to filter
 * @param allCredentials - The full set of credentials to check source_id references against.
 *   Defaults to `credentials` when not provided.
 */
export const filterOutOrphanClones = (
  credentials: OrgCredential[],
  allCredentials?: OrgCredential[]
): OrgCredential[] => {
  const lookupIds = new Set((allCredentials ?? credentials).map((c) => c.id));
  return credentials.filter((cred) => {
    if (!cred.source_id) return true;
    return lookupIds.has(cred.source_id);
  });
};

export const getCredentialsToDisplay = (
  credentials: OrgCredential[],
  orderingFields: string[],
  currentUserId?: string | null
): OrgCredential[] => {
  // Filter out invalid, orphan, and duplicate credentials
  // Note: orphan check uses the original list so that a clone isn't removed just
  // because its shared parent failed validity checks (e.g. expired/inactive).
  let creds = filterValidCredentials(credentials);
  creds = filterOutOrphanClones(creds, credentials);
  creds = filterOutDuplicateCredentials(creds, currentUserId);

  // Sort by fields in `orderingFields` sequentially - null values go to the end and will be sorted by the next field
  return _.orderBy(
    creds,
    orderingFields.map(
      (field) => (cred) =>
        getTimestamp(cred[field as keyof OrgCredential] as string | undefined)
    ),
    orderingFields.map(() => 'desc')
  );
};
